import { Outlet, Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Link, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '@components/LanguageSelector';
import { useHomeDestination } from '@hooks/useHomeDestination';

/**
 * Layout for authentication pages (login, register, password reset)
 * Centered card layout without sidebar navigation
 */
function AuthLayout() {
  const { t } = useTranslation();
  // Signed out, "/" only redirects to sign-in (REV-939), so go there directly.
  const home = useHomeDestination();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: 'background.default',
      }}
    >
      {/* Skip link for keyboard users */}
      <Link href="#main-content" className="skip-link">
        {t('common.skipToContent')}
      </Link>

      {/* Header */}
      <Box
        component="header"
        sx={{
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Link
          component={RouterLink}
          to={home.to}
          sx={{
            textDecoration: 'none',
            color: 'primary.main',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {t('common.appName')}
          </Typography>
        </Link>
        <LanguageSelector />
      </Box>

      {/* Main content area */}
      <Container
        component="main"
        id="main-content"
        tabIndex={-1}
        maxWidth="sm"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Paper
          elevation={2}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 2,
          }}
        >
          <Outlet />
        </Paper>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('common.copyright', '© {{year}} AFixt.', { year: new Date().getFullYear() })}{' '}
          {t('common.allRightsReserved')}
        </Typography>
      </Box>
    </Box>
  );
}

export default AuthLayout;
